/**
 * QuantityInput Component - Enhanced with Inventory Constraints
 * Now properly handles unitAvailable limits and provides user feedback
 * Styled according to Stash House Design Standards
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ProductQuantityCalculator } from '../../utils/ProductQuantityCalculator';
import { appInsights } from '../../appInsights.js';


const QuantityInput = ({ 
  value = 0, 
  onChange, 
  min = 0, 
  max, // Can be overridden by inventory constraints
  size = 'md',
  disabled = false,
  className = '',
  placeholder = '0',
  isUpdating = false,
  showCartIndicator = false,
  onFocusChange,
  // NEW: Product and quantity context for inventory validation
  quantityType = 'total', // 'cases', 'units', or 'total'
  product = null,
  currentCaseQuantity = 0,
  currentUnitQuantity = 0,
  label = null
}) => {
  
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Create calculator for inventory constraints
  // Sell types: 1=Unit Only, 2=Case Only, 3=Cases & Units
  // canSellByUnit: type 1 or 3, canSellByCase: type 2 or 3
  const calculator = useMemo(() => {
    return product ? new ProductQuantityCalculator(product) : null;
  }, [product?.id, product?.unitsPerCase, product?.optSellByTypeID, product?.unitAvailable]);

  // Calculate dynamic max values based on inventory
  const inventoryConstraints = useMemo(() => {
    // Cart quantities are capped to current stock. (The back-order input passes a product without
    // unitAvailable, which also lands here unconstrained — back orders capture demand beyond stock.)
    if (!calculator || !product?.unitAvailable) {
      return {
        maxValue: max || 999,
        maxCases: Math.floor((max || 999) / (product?.unitsPerCase || 1)),
        maxUnits: (product?.unitsPerCase || 1) - 1,
        hasConstraints: false,
        availableText: null
      };
    }

    const unitAvailable = product.unitAvailable;
    const unitsPerCase = product.unitsPerCase || 1;

    if (quantityType === 'cases') {
      // For case input: calculate max cases considering current unit selection
      const unitsReservedByOtherUnits = currentUnitQuantity || 0;
      const unitsAvailableForCases = Math.max(0, unitAvailable - unitsReservedByOtherUnits);
      const maxCases = Math.floor(unitsAvailableForCases / unitsPerCase);
      
      return {
        maxValue: Math.min(max || 999, maxCases),
        maxCases,
        maxUnits: 0,
        hasConstraints: true,
        availableText: `${maxCases} ${calculator?.caseLabelPlural?.toLowerCase() || 'cases'} available`,
        remainingUnits: unitsAvailableForCases
      };
    } else if (quantityType === 'units') {
      // For unit input: calculate max units considering current case selection
      const unitsUsedByCases = (currentCaseQuantity || 0) * unitsPerCase;
      const unitsAvailableForIndividualUnits = Math.max(0, unitAvailable - unitsUsedByCases);
      const maxUnits = Math.min(unitsPerCase - 1, unitsAvailableForIndividualUnits);
      
      return {
        maxValue: Math.min(max || 999, maxUnits),
        maxCases: 0,
        maxUnits,
        hasConstraints: true,
        availableText: `${maxUnits} ${calculator?.unitLabelPlural?.toLowerCase() || 'units'} available`,
        remainingUnits: unitsAvailableForIndividualUnits
      };
    } else {
      // For total quantity input
      return {
        maxValue: Math.min(max || 999, unitAvailable),
        maxCases: Math.floor(unitAvailable / unitsPerCase),
        maxUnits: unitAvailable % unitsPerCase,
        hasConstraints: true,
        availableText: `${unitAvailable} ${calculator?.unitLabelPlural?.toLowerCase() || 'units'} available`,
        remainingUnits: unitAvailable
      };
    }
  }, [calculator, product?.unitAvailable, product?.unitsPerCase, quantityType, currentCaseQuantity, currentUnitQuantity, max]);

  // Validate value against constraints
  const validateValue = (inputValue) => {
    if (!calculator || !product) return null;

    if (inputValue > inventoryConstraints.maxValue) {
      if (quantityType === 'cases') {
        return `Only ${inventoryConstraints.maxValue} ${calculator?.caseLabelPlural?.toLowerCase() || 'cases'} available (${inventoryConstraints.remainingUnits} ${calculator?.unitLabelPlural?.toLowerCase() || 'units'} remaining)`;
      } else if (quantityType === 'units') {
        return `Only ${inventoryConstraints.maxValue} individual ${calculator?.unitLabelPlural?.toLowerCase() || 'units'} available`;
      } else {
        return `Only ${inventoryConstraints.maxValue} ${calculator?.unitLabelPlural?.toLowerCase() || 'units'} available`;
      }
    }

    if (quantityType === 'units' && inputValue >= (product.unitsPerCase || 1)) {
      return `Individual ${calculator?.unitLabelPlural?.toLowerCase() || 'units'} must be less than ${product.unitsPerCase}. Use ${calculator?.caseLabelPlural?.toLowerCase() || 'cases'} for larger quantities.`;
    }

    return null;
  };

  // Update validation when value or constraints change
  useEffect(() => {
    const error = validateValue(value);
    setValidationError(error);
  }, [value, inventoryConstraints.maxValue, quantityType]);

  const handleIncrement = () => {
    const newValue = value + 1;
    const error = validateValue(newValue);

    if (error) {
      // Show validation error but don't change value
      setValidationError(error);
      return;
    }

    if (newValue <= inventoryConstraints.maxValue && !disabled) {
      onChange(newValue);
      setValidationError(null);
      if (appInsights) {
        appInsights.trackEvent({ name: 'QuantityChanged' }, {
          direction: 'increment',
          quantityType,
          newValue,
          productId: String(product?.id || ''),
          source: 'quantityInput',
        });
      }
    }
  };

  const handleDecrement = () => {
    if (value > min && !disabled) {
      onChange(value - 1);
      setValidationError(null);
      if (appInsights) {
        appInsights.trackEvent({ name: 'QuantityChanged' }, {
          direction: 'decrement',
          quantityType,
          newValue: value - 1,
          productId: String(product?.id || ''),
          source: 'quantityInput',
        });
      }
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty string for user typing
    if (inputValue === '') {
      onChange(0);
      setValidationError(null);
      return;
    }

    // Parse and validate number
    const numValue = parseInt(inputValue, 10);
    if (!isNaN(numValue)) {
      const error = validateValue(numValue);
      
      if (error) {
        // Set the value but show validation error
        setValidationError(error);
      } else {
        setValidationError(null);
      }
      
      // Clamp to absolute bounds (min/max)
      const clampedValue = Math.max(min, Math.min(inventoryConstraints.maxValue, numValue));
      onChange(clampedValue);
    }
  };

  const handleInputBlur = (e) => {
    // Ensure value is valid on blur
    const inputValue = parseInt(e.target.value, 10);
    if (isNaN(inputValue) || inputValue < min) {
      onChange(min);
    } else if (inputValue > inventoryConstraints.maxValue) {
      onChange(inventoryConstraints.maxValue);
      setValidationError(`Adjusted to maximum available: ${inventoryConstraints.maxValue}`);
      // Clear validation error after 3 seconds
      setTimeout(() => setValidationError(null), 3000);
    }
    
    setIsFocused(false);
    if (onFocusChange) {
      onFocusChange(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setValidationError(null); // Clear validation error on focus
    if (onFocusChange) {
      onFocusChange(true);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const sizeClass = size === 'sm' ? 'input-group-sm' : size === 'lg' ? 'input-group-lg' : '';
  
  // Determine visual state
  const hasValue = value > 0;
  const isLoading = isUpdating;
  const showAsActive = hasValue || isFocused || isHovered;
  const hasError = !!validationError;
  const isAtMax = value >= inventoryConstraints.maxValue;

  return (
    <div className={`stash-quantity-input__container  ${hasError ? 'has-validation-error' : ''}`}>
      {/* Input row: input-group + optional label */}
      <div className="d-flex align-items-center gap-1">
        <div
          className={`input-group stash-quantity-input__group ${sizeClass} ${className} ${showAsActive ? 'stash-quantity-input__group--active' : ''} ${isLoading ? 'stash-quantity-input__group--loading' : ''} ${hasError ? 'stash-quantity-input__group--error' : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Decrement Button */}
          <button
            type="button"
            className={`btn btn-outline-secondary stash-quantity-input__btn stash-quantity-input__btn--decrement stash-quantity-input__btn--success`}
            onClick={handleDecrement}
            disabled={disabled || value <= min || isLoading}
            aria-label="Decrease quantity"
          >
            {isLoading && value > 0 ? (
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <i className="bi bi-dash"></i>
            )}
          </button>

          {/* Number Input */}
          <div className="stash-quantity-input__field-container">
            <input
              type="number"
              className={`form-control stash-quantity-input__field text-center ${hasValue ? 'stash-quantity-input__field--has-value' : ''} ${hasError ? 'is-invalid' : ''}`}
              value={value}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              min={min}
              max={inventoryConstraints.maxValue}
              disabled={disabled || isLoading}
              placeholder={placeholder}
              aria-label="Quantity"
            />

            {/* Cart indicator */}
            {showCartIndicator && hasValue && (
              <div className="stash-quantity-input__cart-indicator">
                <i className="bi bi-cart-check-fill"></i>
              </div>
            )}

            {/* Loading overlay */}
            {isLoading && (
              <div className="stash-quantity-input__loading-overlay">
                <div className="spinner-border spinner-border-sm text-stash-primary" role="status">
                  <span className="visually-hidden">Updating...</span>
                </div>
              </div>
            )}
          </div>

          {/* Increment Button */}
          <button
            type="button"
            className={`btn btn-outline-secondary stash-quantity-input__btn stash-quantity-input__btn--increment stash-quantity-input__btn--success' ${isAtMax ? 'stash-quantity-input__btn--warning' : ''}`}
            onClick={handleIncrement}
            disabled={disabled || value >= inventoryConstraints.maxValue || isLoading}
            aria-label="Increase quantity"
            title={isAtMax ? `Maximum available: ${inventoryConstraints.maxValue}` : 'Increase quantity'}
          >
            <i className="bi bi-plus"></i>
          </button>
        </div>

        {/* Label sits right next to the input-group, always aligned */}
        {label && (
          <small className="text-muted cart-qty-label" style={{whiteSpace: 'nowrap'}}>{label}</small>
        )}
      </div>

      {/* Visible message when at max — below input row */}
      {isAtMax && !hasError && !isLoading && inventoryConstraints.hasConstraints && (
        <div className="mt-1">
          <small className="text-danger" style={{fontSize:'.75rem'}}>
            <i className="bi bi-info-circle me-1"></i>
            {inventoryConstraints.maxValue === 0
              ? `Not enough stock for a full ${calculator?.caseLabel?.toLowerCase() || 'case'}`
              : `Max ${inventoryConstraints.availableText}`}
          </small>
        </div>
      )}
    </div>
  );
};

export default QuantityInput;